import React, { useEffect } from "react";
import moment from "moment";
import { Clock, User, Package, Calendar, Users, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../App";
import Skeleton from "../ui/Skeleton";
import { getGroupColor } from "../../hooks/sessionGrouping";

const SessionListView = ({
  sessions,
  loading,
  trainers,
  onSessionClick,
  currentDate,
  calendarView,
  ignoreViewFilter,
  onUpdateStatus,
  updatingSessionIds = [],
}) => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  // Filter sessions based on calendar view and current date
  const getFilteredSessions = () => {
    // If we're in a custom view mode (like List view with custom range),
    // do not filter by calendarView logic. Show all loaded sessions.
    if (ignoreViewFilter) {
      return sessions;
    }

    const filtered = sessions.filter((item) => {
      const sessionDate = moment(item.start);

      switch (calendarView) {
        case "daily":
          return sessionDate.isSame(currentDate, "day");
        case "weekly":
          return sessionDate.isSame(currentDate, "week");
        case "monthly":
          return sessionDate.isSame(currentDate, "month");
        default:
          return true;
      }
    });

    return filtered;
  };

  // Get status gradient and text
  const getStatusConfig = (status) => {
    switch (status) {
      case "completed":
        return {
          gradient: "from-green-500 to-green-600",
          text: t("sessionsPage.filters.statuses.completed"),
        };
      case "cancelled":
        return {
          gradient: "from-gray-500 to-gray-600",
          text: t("sessionsPage.filters.statuses.cancelled"),
        };
      case "no-show":
        return {
          gradient: "from-red-500 to-red-600",
          text: t("sessionsPage.filters.statuses.noShow"),
        };
      case "scheduled":
      default:
        return {
          gradient: "from-blue-500 to-blue-600",
          text: t("sessionsPage.filters.statuses.scheduled"),
        };
    }
  };

  // Format session time
  const formatSessionTime = (start, end) => {
    return `${moment(start).format("HH:mm")} - ${moment(end).format("HH:mm")}`;
  };

  // Format session date
  const formatSessionDate = (date) => {
    return moment(date).format("DD MMMM YYYY, dddd");
  };

  // Group sessions by date
  const groupSessionsByDate = () => {
    const filteredSessions = getFilteredSessions();
    const grouped = {};

    filteredSessions.forEach((item) => {
      const date = moment(item.start).format("YYYY-MM-DD");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(item);
    });

    const sortedDates = Object.keys(grouped).sort();
    return sortedDates.map((date) => ({
      date,
      sessions: grouped[date].sort((a, b) =>
        moment(a.start).diff(moment(b.start)),
      ),
    }));
  };

  // Get trainer name
  const getTrainerName = (trainerId) => {
    const trainer = trainers.find((t) => t.id === trainerId);
    return trainer ? trainer.name : t("sessionsPage.listView.unknownTrainer");
  };

  // Render status dropdown helper
  const renderStatusDropdown = (sessionToUpdate) => {
    if (!sessionToUpdate) return null;
    
    const sId = sessionToUpdate.id || sessionToUpdate.SK;
    const isUpdating = updatingSessionIds.includes(sId);

    const isStatusScheduled = (sessionToUpdate.status || "scheduled") === "scheduled";
    const isDisabledForTrainer = user?.role === 'trainer' && !isStatusScheduled;
    const sessionStatusConfig = getStatusConfig(sessionToUpdate.status);

    return (
      <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <select
            value={sessionToUpdate.status}
            disabled={isDisabledForTrainer || isUpdating}
            onChange={(e) =>
              onUpdateStatus &&
              onUpdateStatus(sId, e.target.value)
            }
            className={`appearance-none pl-2 pr-6 sm:pl-3 sm:pr-8 py-1.5 bg-gradient-to-r ${sessionStatusConfig.gradient} text-white rounded-xl text-[10px] sm:text-[11px] font-bold shadow-sm ${isDisabledForTrainer || isUpdating ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:opacity-90 active:scale-95'} border-none outline-none focus:ring-2 focus:ring-blue-400/50 transition-all w-[85px] sm:w-[100px] text-ellipsis overflow-hidden`}
          >
            <option value="scheduled" className="bg-blue-600 text-white">{t("sessionsPage.filters.statuses.scheduled")}</option>
            <option value="completed" className="bg-green-600 text-white">{t("sessionsPage.filters.statuses.completed")}</option>
            <option value="cancelled" className="bg-gray-600 text-white">{t("sessionsPage.filters.statuses.cancelled")}</option>
            <option value="no-show" className="bg-red-600 text-white">{t("sessionsPage.filters.statuses.noShow")}</option>
          </select>
          {isUpdating ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-xl pointer-events-none">
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
          ) : (
            <ChevronDown
              className="absolute right-1.5 sm:right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white pointer-events-none"
            />
          )}
        </div>
      </div>
    );
  };

  // Render session item
  const renderSessionItem = (item) => {
    if (item.isGroupContainer) {
      // GROUP CONTAINER
      const groupColor = getGroupColor(item.groupId);

      return (
        <div
          key={item.id}
          className={`p-4 rounded-lg border-2 ${groupColor.border} ${groupColor.bg} mb-3 transition-all hover:shadow-lg`}
        >
          {/* Group Header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-600/30">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 ${groupColor.bg} ${groupColor.border} border rounded-lg`}
              >
                <Users className={`w-5 h-5 ${groupColor.text}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`font-bold ${colors.textPrimary}`}>
                    {t("sessionsPage.listView.groupSession")}
                  </span>
                  <span
                    className={`px-2 py-1 ${groupColor.bg} ${groupColor.border} border rounded-full text-xs font-bold ${groupColor.text}`}
                  >
                    {t("sessionsPage.listView.studentsCount", {
                      count: item.groupSize,
                    })}
                  </span>
                </div>
                <div className={`text-sm ${colors.textSecondary} mt-0.5`}>
                  {item.packageName}
                </div>
              </div>
            </div>

            <div className="text-right">
              <div
                className={`flex items-center text-sm ${colors.textSecondary} mb-1`}
              >
                <Clock className={`h-4 w-4 mr-1.5 ${colors.textTertiary}`} />
                {formatSessionTime(item.start, item.end)}
              </div>
              <div
                className={`flex items-center text-sm ${colors.textSecondary}`}
              >
                <User className={`h-4 w-4 mr-1.5 ${colors.textTertiary}`} />
                {getTrainerName(item.trainerId)}
              </div>
            </div>
          </div>

          {/* Students List */}
          <div className="space-y-2">
            {item.students.map((student, idx) => (
              <div
                key={student.id}
                onClick={() => onSessionClick(student.session)}
                className={`
                  flex items-center justify-between p-3 rounded-lg cursor-pointer
                  transition-all hover:scale-[1.01]
                  ${colors.cardBg} border ${colors.cardBorder} hover:border-purple-500
                `}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full bg-gradient-to-br ${groupColor.bg} border-2 ${groupColor.border} flex items-center justify-center font-bold ${groupColor.text}`}
                  >
                    {idx + 1}
                  </div>
                  <div>
                    <div className={`font-semibold ${colors.textPrimary}`}>
                      {student.studentName}
                    </div>
                    {student.notes && (
                      <div
                        className={`text-xs ${colors.textMuted} mt-0.5 truncate max-w-md`}
                      >
                        {student.notes}
                      </div>
                    )}
                  </div>
                </div>

                {renderStatusDropdown(student.session)}
              </div>
            ))}
          </div>

          {/* Footer Info */}
          <div
            className={`mt-3 pt-3 border-t border-gray-600/30 flex items-center justify-between text-xs ${colors.textTertiary}`}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                <span>
                  {moment(item.end).diff(moment(item.start), "minutes")}{" "}
                  {t("sessionsPage.listView.minutes")}
                </span>
              </div>
            </div>
            <span className={groupColor.text}>
              {t("sessionsPage.listView.groupSession")}
            </span>
          </div>
        </div>
      );
    } else {
      // SOLO SESSION
      return (
        <div
          key={item.id}
          onClick={() => onSessionClick(item)}
          className={`
            p-4 rounded-lg cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] mb-2
            ${colors.cardBg} border ${colors.cardBorder} hover:border-purple-500
          `}
        >
          <div className="flex items-start sm:items-center justify-between gap-2">
            <div className="flex-1 min-w-0 pr-1 sm:pr-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-3 mb-1.5 sm:mb-2">
                <span
                  className={`text-base font-semibold ${colors.textPrimary}`}
                >
                  {item.studentName}
                </span>
                <div
                  className={`flex items-center text-sm ${colors.textSecondary}`}
                >
                  <Clock className={`h-4 w-4 mr-1.5 ${colors.textTertiary}`} />
                  {formatSessionTime(item.start, item.end)}
                </div>
              </div>

              {item.notes && (
                <div
                  className={`text-xs ${colors.textMuted} mb-2 truncate max-w-xl`}
                >
                  {item.notes}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-4 mt-1">
                <div
                  className={`flex items-center text-sm ${colors.textSecondary}`}
                >
                  <User className={`h-4 w-4 mr-1.5 ${colors.textTertiary}`} />
                  {getTrainerName(item.trainerId)}
                </div>

                <div
                  className={`flex items-center text-sm ${colors.textSecondary}`}
                >
                  <Package
                    className={`h-4 w-4 mr-1.5 ${colors.textTertiary}`}
                  />
                  <span className="truncate max-w-xs">
                    {item.packageName || t("sessionsPage.listView.noPackage")}
                  </span>
                </div>

                <div className={`text-sm ${colors.textSecondary}`}>
                  {moment(item.end).diff(moment(item.start), "minutes")}{" "}
                  {t("sessionsPage.listView.minutes")}
                </div>
              </div>
            </div>

            {renderStatusDropdown(item)}
          </div>
        </div>
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className={`${colors.cardBg} rounded-2xl ${colors.cardBorder} p-6 shadow-lg space-y-4`}>
             <div className="flex justify-between">
                <Skeleton width="40%" height="6" />
                <Skeleton width="20%" height="6" />
             </div>
             <Skeleton width="100%" height="4" className="opacity-40" />
             <div className="flex gap-4">
                <Skeleton width="100px" height="4" />
                <Skeleton width="80px" height="4" />
             </div>
          </div>
        ))}
      </div>
    );
  }

  const groupedSessions = groupSessionsByDate();

  if (groupedSessions.length === 0) {
    const periodText =
      calendarView === "daily"
        ? t("sessionsPage.listView.period.today")
        : calendarView === "weekly"
          ? t("sessionsPage.listView.period.thisWeek")
          : t("sessionsPage.listView.period.thisMonth");

    return (
      <div
        className={`${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg p-12 text-center`}
      >
        <div
          className={`w-20 h-20 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}
        >
          <Calendar className={`w-10 h-10 ${colors.emptyStateIcon}`} />
        </div>
        <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2`}>
          {calendarView === "daily" && t("sessionsPage.listView.empty.daily")}
          {calendarView === "weekly" && t("sessionsPage.listView.empty.weekly")}
          {calendarView === "monthly" &&
            t("sessionsPage.listView.empty.monthly")}
        </h3>
        <p className={colors.textTertiary}>
          {t("sessionsPage.listView.empty.description", { period: periodText })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedSessions.map(({ date, sessions: dateSessions }) => (
        <div
          key={date}
          className={`${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg overflow-hidden`}
        >
          {/* Date Header */}
          <div
            className={`px-6 py-4 border-b ${colors.tableBorder} ${colors.tableHeader}`}
          >
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-bold ${colors.textPrimary}`}>
                {formatSessionDate(date)}
              </h2>
              <span
                className={`text-sm px-4 py-1.5 ${colors.inputBg} ${colors.textSecondary} rounded-full border ${colors.inputBorder} font-semibold`}
              >
                {t("sessionsPage.listView.sessionCount", {
                  count: dateSessions.length,
                })}
              </span>
            </div>
          </div>

          {/* Sessions */}
          <div className="p-4">
            {dateSessions.map((item) => renderSessionItem(item))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SessionListView;
